import React from "react";
import { Link } from "react-router-dom";
import { useLearningRecord } from "../../../api/queries/learningQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import {
  PARTICIPANT_STATUS,
  formatDay,
  formatSchedule,
  titleCase,
} from "./learningFormat";
import "../learning.css";

const Total = ({ label, value }) => (
  <div className="learn-total">
    <span className="approval-meta-label">{label}</span>
    <strong className="tabular-nums">{value ?? 0}</strong>
  </div>
);

/** Employee profile → Learning: totals, skills register and seminars attended. */
const EmployeeLearningRecord = ({ employeeId }) => {
  const query = useLearningRecord(employeeId);
  const r = query.data;

  if (query.isLoading)
    return (
      <div className="text-center py-4">
        <span
          className="spinner-border text-primary"
          role="status"
          aria-label="Loading"
        ></span>
      </div>
    );
  if (query.isError)
    return (
      <div className="alert alert-danger fs-085 m-0">
        {getApiErrorMessage(query.error, "Could not load the learning record.")}
      </div>
    );
  if (!r) return null;

  const t = r.totals || {};
  const skills = r.skills || [];
  const attended = r.attended || [];

  return (
    <div className="d-flex flex-column gap-3">
      <div className="learn-totals">
        <Total label="Attended" value={t.attended} />
        <Total label="Hours" value={t.hours} />
        <Total label="Upcoming" value={t.upcoming} />
        <Total label="Pending" value={t.pending} />
        <Total label="Absent" value={t.absent} />
        <Total label="Excused" value={t.excused} />
      </div>

      <div>
        <h4 className="learn-subtitle">Skills</h4>
        {skills.length === 0 ? (
          <p className="fs-085 text-slate-500 mb-0">
            No skills yet: skills are added when the employee attends a seminar
            linked to them.
          </p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm mb-0 learn-table">
              <thead>
                <tr>
                  <th>Skill</th>
                  <th>Category</th>
                  <th className="text-end">Seminars</th>
                  <th className="text-end">Hours</th>
                  <th>First · Last attended</th>
                </tr>
              </thead>
              <tbody>
                {skills.map((s) => (
                  <tr key={s.name}>
                    <td className="fw-semibold">{s.name}</td>
                    <td>{s.category || "—"}</td>
                    <td className="text-end tabular-nums">{s.seminars ?? 0}</td>
                    <td className="text-end tabular-nums">{s.hours ?? 0}</td>
                    <td className="fs-075 text-nowrap">
                      {formatDay(s.firstAttendedAt)} ·{" "}
                      {formatDay(s.lastAttendedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h4 className="learn-subtitle">Seminars attended</h4>
        {attended.length === 0 ? (
          <p className="fs-085 text-slate-500 mb-0">None yet.</p>
        ) : (
          <ul className="learn-attended">
            {attended.map((s) => {
              const meta =
                PARTICIPANT_STATUS[s.status] || PARTICIPANT_STATUS.ATTENDED;
              return (
                <li key={s.id || s.seminarId}>
                  <div className="d-flex justify-content-between gap-2">
                    <Link
                      to={`/learning/seminars/${s.id || s.seminarId}`}
                      className="fw-semibold text-decoration-none"
                    >
                      {s.title}
                    </Link>
                    <span className={`status-pill status-pill--${meta.tone}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="fs-075 text-slate-500">
                    {s.type ? `${titleCase(s.type)} · ` : ""}
                    {formatSchedule(s.startAt, s.endAt)}
                    {s.hours ? ` · ${s.hours} h` : ""}
                  </div>
                  {(s.skills || []).length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {s.skills.map((k) => (
                        <span key={k.id || k.name} className="chip chip--sm">
                          {k.name}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EmployeeLearningRecord;
